import React, { useEffect, useState } from 'react';
import { Grid, Button, TextField, Select, MenuItem, Typography, Box, Divider, FormControl, InputLabel, FormHelperText } from '@mui/material';
import DefaultLayout from "../../components/layout/DefaultLayout";
import FormBlocContainer from "../../components/layout/form/FormBlocContainer";
import PrestationsService from "../../api/prestations/services/PrestationsService";
import { useSelector } from "react-redux";
import Loader from '../../components/berry/ui-component/Loader';
import useMessageNotifier from "../../helpers/hooks/component/useMessageNotifier";
import UploadFiles from './UploadFiles';
import { useTheme } from "@mui/material/styles";
import { Tooltip } from '@mui/material';


const dossierRegex = /^[0-9]{9}[A-Za-z]$/;

const ContactPrestation = () => {
    const account = useSelector(state => state.account);
    const displayName = account.user.displayName;
    const firstName = account.profil.firstname;
    const lastName = account.profil.lastname;

    const [options, setOptions] = useState(null);
    const [loadingContact, setLoadingContact] = useState(false);
    const [message, setMessage] = useState('');
    const { showMessage } = useMessageNotifier();
    const theme = useTheme();

    const services = [
        {
            value: "INFORMATIONS_PRESTATIONS",
            label: 'Une déclaration de sinistre ou une prolongation d\'arrêt',
            infobulle: 'si vous ne savez pas quel motif choisir, sélectionnez "arrêt de travail"',
            options: [
                { value: 'INSURED_SEND_INFORMATIONS_PRESTATIONS_WORK', label: 'Il s\'agit d\'un arrêt de travail', risque: '030 Incapacité' },
                { value: 'INSURED_SEND_INFORMATIONS_PRESTATIONS_DISABLE', label: 'Il s\'agit d\'une invalidité', risque: '040 Invalidité' },
                { value: 'INSURED_SEND_INFORMATIONS_PRESTATIONS_DEATH', label: 'Il s\'agit d\'un décès', risque: '0C1 Décès' },
            ]
        },
        {
            value:"INFORMATIONS_COTISATIONS",
            label: 'Des questions sur votre indemnisation',
            infobulle: 'si vous ne savez pas quel motif choisir, sélectionnez "arrêt de travail"',
            options: [
                { value: 'INSURED_SEND_INFORMATIONS_INDEMNISATIONS_WORK', label: 'Il s\'agit d\'un arrêt de travail', risque: '030 Incapacité' },
                { value: 'INSURED_SEND_INFORMATIONS_INDEMNISATIONS_DISABLE', label: 'Il s\'agit d\'une invalidité', risque: '040 Invalidité' },
                { value: 'INSURED_SEND_INFORMATIONS_INDEMNISATIONS_DEATH', label: 'Il s\'agit d\'un décès', risque: '0C1 Décès' },
            ]
        },
        { 
            value: "INFORMATIONS_ADMINISTRATIVES", 
            label: "Modification des données administratives",
            options: [
                { value: 'INSURED_SEND_AUTRES_DEMANDES_ADDRESS_CHANGE', label: 'Déclarer un changement d\'adresse postale', risque: '040 Invalidité' },
                { value: 'INSURED_SEND_AUTRES_DEMANDES_RIB_CHANGE', label: 'Déclarer un changement de RIB', risque: '040 Invalidité'},
                { value: 'INSURED_SEND_AUTRES_DEMANDES_OTHER', label: 'Autre', risque: '040 Invalidité' },
            ]
        },
        { 
            value: "RECLAMATION",
            label: "Une réclamation"
        }
    ];

    const [contactData, setContactData] = useState({
        categorie: 'INFORMATIONS_PRESTATIONS',
        lastName: firstName,
        firstName: lastName,
        email: account.user.email,
        message: '',
        ssNum: account.profil.ssNum,
        dossierNumber: '',
        service: '',
        risque: ''
    });

    const [files, setFiles] = useState([]);
    const [uploadError, setUploadError] = useState("");
    const [dossierError, setDossierError] = useState("");
    const [messageError, setMessageError] = useState("");
    const [motifError, setMotifError] = useState("");

    useEffect(() => {
        const servicesOptions = services.find(service => service.value === contactData.categorie && service.options);
        setOptions(servicesOptions?.options || null);
    }, [contactData.categorie]);

    const handleContactChange = (event) => {
        
        const { name, value } = event.target;

        const servicesOptions = services.find(service => service.value === contactData.categorie && service.options);
        if(options !== servicesOptions?.options) {
            setContactData(prev => ({ ...prev, subService: '' }));
            setOptions(servicesOptions?.options || null);
        }

        if (name === 'dossierNumber') {
            // Validate only if user has entered something
            if (value && !dossierRegex.test(value)) {
                setDossierError('Saisie non valide. Merci de saisir un numéro composé de 9 chiffres et d’une lettre, exemple : 202521542M');
            } else {
                setDossierError('');
            }
        }


        if (name === 'categorie') {
            // Reset service and risque when category changes
            const category = services.find(service => service.value === value);
            if (category && !category.options) {
                setContactData(prev => ({ ...prev, risque: category.risque, service: value }));
            }else{
                setContactData(prev => ({ ...prev, risque: '', service: '' }));
            }
        }


        if (name === 'service' && options) {
            // Set risque based on selected service
            const selectedOption = options.find(option => option.value === value);
            if (selectedOption && selectedOption.risque) {
                setContactData(prev => ({ ...prev, risque: selectedOption.risque, service: value }));
            }
            // Clear motif error when user selects a value
            if (value) {
                setMotifError('');
            }
        }

        setContactData(prev => ({ ...prev, [name]: value }));
    };

    const handleContactSubmit = async (event) => {

        event.preventDefault();
        setLoadingContact(true);
        setMessage("");

        // If dossier error, block submission
        if (dossierError) {
            showMessage(dossierError, "error");
            setLoadingContact(false);
            return;
        }

        if (uploadError) {
            showMessage(uploadError, "error");
            setLoadingContact(false);
            return;
        }

        // Validate motif field when options are available
        if (options && !contactData.service) {
            showMessage("Le motif ne peut pas être vide", "error");
            setMotifError("Le motif ne peut pas être vide");
            setLoadingContact(false);
            return;
        }

        if(!contactData.message.trim()) {
            showMessage("Le message ne peut pas être vide.", "error");
            setMessageError("Le message ne peut pas être vide.");
            setLoadingContact(false);
            return;
        }
        
        try {
            await PrestationsService.sendMailInsured(contactData, files);
            showMessage('Votre message a été envoyé avec succès !', 'success');
            setFiles([]);
            setContactData(prev => ({ ...prev, message: "", dossierNumber: "" }));
        } catch (error) {
            console.error('Error submitting contact form:', error);
            showMessage("Une erreur est survenue lors de l'envoi de votre message", 'error');
        } finally {
            setLoadingContact(false);
        }
    };

    const reclamationLayout = 
        <>
            <Grid item xs={12}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', marginBottom: theme.spacing(2) }}>
                    Réclamation
                </Typography>
                <Typography sx={{ fontSize: '16px', marginBottom: theme.spacing(2) }}>
                    Pour toute réclamation, nous vous invitons à consulter ce lien : <a href="https://www.uniprevoyance.fr/formulaire-reclamation/" target="_blank" rel="noopener noreferrer">https://www.uniprevoyance.fr/formulaire-reclamation/</a>
                </Typography>
            </Grid>
        </>

    const classicFormLayout =
        <>
            <Grid item xs={12} sm={6}>
                <TextField
                    name="lastName"
                    variant="outlined"
                    value={firstName}
                    fullWidth
                    required
                    disabled
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    name="firstName"
                    variant="outlined"
                    value={lastName}
                    fullWidth
                    required
                    disabled
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    name="email"
                    variant="outlined"
                    label="Email"
                    value={account.user.email}
                    fullWidth
                    required
                    type="email"
                    disabled
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    label="N° dossier"
                    name="dossierNumber"
                    variant="outlined"
                    value={contactData.dossierNumber}
                    fullWidth
                    onChange={handleContactChange}
                    error={Boolean(dossierError)}
                    helperText={dossierError || ''}
                    inputProps={{ maxLength: 10 }}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Message"
                    error={Boolean(messageError)}
                    name="message"
                    variant="outlined"
                    value={contactData.message}
                    fullWidth
                    required
                    multiline
                    minRows={4}
                    onChange={handleContactChange}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <UploadFiles
                    files={files}
                    setFiles={setFiles}
                    error={uploadError}
                    setError={setUploadError}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Grid container justifyContent="flex-end">
                    <Button
                        variant="contained"
                        type="submit"
                        color="primary"
                        disabled={loadingContact || Boolean(uploadError) || Boolean(dossierError) || Boolean(motifError)}
                        sx={{ textTransform: 'none' }}
                    >
                        {loadingContact ? 'Envoi en cours...' : 'Envoyer'}
                    </Button>
                </Grid>
            </Grid>
        </>

    return (
        <DefaultLayout>
            <Box sx={{ p: 2, bgcolor: 'transparent', borderRadius: '8px', width: '100%' }}>
                <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
                    Ma messagerie
                </Typography>
                <Divider sx={{
                    borderColor: '#EF595D',
                    my: 1,
                    width: '4%',
                    borderWidth: '2px',
                    marginBottom: '15px',
                    marginTop: '15px'
                }} />
                <Typography sx={{ fontSize: '16px' }}>
                    Afin de diriger votre mail vers le service concerné, merci de bien vouloir qualifier votre demande :
                </Typography>
            </Box>
            <FormBlocContainer>
                {loadingContact && <Loader />}

                <form onSubmit={handleContactSubmit} noValidate>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required variant="outlined">
                                <InputLabel id="categorie-label">Motif de la demande</InputLabel>
                                    <Select
                                        labelId="categorie-label"
                                        label="Motif de la demande"
                                        name="categorie"
                                        value={contactData.categorie}
                                        onChange={handleContactChange}
                                        fullWidth
                                        required
                                        inputProps={{
                                            sx: { fontSize: { xs: '16px', sm: '18px' } }
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    '& .MuiMenuItem-root': {
                                                        fontSize: { xs: '16px', sm: '18px' }
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        {services.map((group, index) => (
                                            <MenuItem key={index} value={group.value}>
                                                {group.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                {options && (
                                    <FormControl fullWidth required variant="outlined" margin="normal" error={Boolean(motifError)}>
                                        <InputLabel id="service-label">Motif</InputLabel>
                                            <Tooltip
                                            title={services.find((s) => s.value === contactData.categorie)?.infobulle || ''}
                                            arrow
                                            disableHoverListener={!services.find((s) => s.value === contactData.categorie)?.infobulle}
                                            placement="top-end"
                                            >
                                                <Select
                                                labelId="service-label"
                                                label="Motif"
                                                name="service"
                                                value={contactData.service}
                                                onChange={handleContactChange}
                                                fullWidth
                                                inputProps={{ sx: { fontSize: { xs: '16px', sm: '18px' } } }}
                                                MenuProps={{
                                                    PaperProps: {
                                                    sx: { '& .MuiMenuItem-root': { fontSize: { xs: '16px', sm: '18px' } } }
                                                    }
                                                }}
                                                >
                                                    {options.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                        </Tooltip>
                                        {motifError && <FormHelperText>{motifError}</FormHelperText>}
                                    </FormControl>
                                )}
                            </FormControl>
                        </Grid>
                        {contactData.categorie !== "RECLAMATION" ? (classicFormLayout) : (reclamationLayout)}
                    </Grid>
                </form>

                {message && (
                    <Box mt={2} color={loadingContact ? 'gray' : 'green'}>
                        {message}
                    </Box>
                )}
            </FormBlocContainer>
        </DefaultLayout>
    );
};

export default ContactPrestation;
